import type { TOC } from '@ember/component/template-only';
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismIcon } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import { t } from 'ember-intl';
import { Illustration } from 'frontend-utils';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import ErnestScribble from 'lumen/assets/images/logos/ernest-scribble.svg?raw';
import GeneralDropdownsDropdownYield, {
  type Dropdown,
} from 'lumen/components/general/dropdowns/dropdown-yield';
import ENV from 'lumen/config/environment';
import {
  ASK_ERNEST_COMPARISONS,
  type AskErnestPage,
  type AskErnestPrompt,
  DEFAULT_ASK_ERNEST_COMPARISON,
} from 'lumen/utils/constants/ask-ernest-constants';

import styles from './ask-ernest.module.css';

interface AskErnestTriggerSignature {
  Args: {
    isOpen: boolean;
    label: string;
    onToggle: (isOpen: boolean) => void;
    toggleDropdown: () => void;
  };
}

const AskErnestTrigger = <template>
  <PrismButton
    @buttonType="intelligence"
    @label={{@label}}
    @onClick={{@toggleDropdown}}
    aria-expanded={{if @isOpen "true" "false"}}
    class="text-nowrap"
    data-test-id="ask-ernest-button"
    {{on "click" (fn @onToggle @isOpen)}}
  />
</template> satisfies TOC<AskErnestTriggerSignature>;

type PromptLink = AskErnestPrompt & { href: string };

interface GeneralAskErnestSignature {
  Args: {
    comparison?: string;
    dateRange: { endDate: Dayjs; startDate: Dayjs };
    page: AskErnestPage;
    prompts: AskErnestPrompt[];
  };
}

export default class GeneralAskErnest extends Component<GeneralAskErnestSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare usageAnalytics: Services['usage-analytics'];

  suggestedId = `ask-ernest-suggested-${guidFor(this)}`;

  get blankChatHref(): string {
    return formatUrl(ENV.ernestURL, { source: this.args.page });
  }

  get comparison(): string {
    const { comparison } = this.args;

    return comparison && ASK_ERNEST_COMPARISONS.includes(comparison)
      ? comparison
      : DEFAULT_ASK_ERNEST_COMPARISON;
  }

  get promptLinks(): PromptLink[] {
    const { dateRange } = this.args;

    const context = {
      comparison: this.comparison,
      endDate: dateRange.endDate.format('YYYY-MM-DD'),
      property: this.hotelAndMarketService.hotel?.hotelinfo?.name ?? '',
      startDate: dateRange.startDate.format('YYYY-MM-DD'),
    };

    return this.args.prompts.map((prompt) => ({
      ...prompt,
      href: formatUrl(ENV.ernestURL, {
        source: this.args.page,
        promptId: prompt.id,
        prompt: prompt.buildMessage(context),
        send: 1,
      }),
    }));
  }

  @action
  trackOpen(wasOpen: boolean): void {
    if (wasOpen) {
      return;
    }

    this.usageAnalytics.track('Ask Ernest Clicked', {
      eventTrigger: 'ask-ernest-button-clicked',
      page: this.args.page,
    });
  }

  @action
  trackOtherQuestion(dropdown: Dropdown): void {
    dropdown.toggleDropdown();
    this.usageAnalytics.track('Other Question Selected', {
      eventTrigger: 'ask-ernest-popover',
      page: this.args.page,
    });
  }

  @action
  trackPrompt(dropdown: Dropdown, prompt: AskErnestPrompt): void {
    dropdown.toggleDropdown();
    this.usageAnalytics.track('Prompt Shortcut Selected', {
      eventTrigger: 'ask-ernest-popover',
      page: this.args.page,
      promptId: prompt.id,
    });
  }

  <template>
    <div class="relative {{styles.wrapper}}">
      <GeneralDropdownsDropdownYield
        @dropdownClass=".ask-ernest-dropdown"
        {{! @glint-expect-error: Incorrect type }}
        @dropdownClassName={{concat "ask-ernest-dropdown " styles.dropdown}}
        @label={{t "general.ask_ernest.button"}}
        @selected={{null}}
        @triggerComponent={{component AskErnestTrigger onToggle=this.trackOpen}}
        as |dropdown|
      >
        <div class="flex flex-col gap-300" data-test-id="ask-ernest-popover">
          <div class="flex items-center gap-200">
            <Illustration
              @html={{ErnestScribble}}
              aria-hidden="true"
              class={{styles.symbol}}
            />
            <h4>{{t "general.ask_ernest.title"}}</h4>
          </div>

          <div class="flex flex-col gap-400">
            <div class="flex flex-col gap-200">
              <p
                class="text-200--regular text-neutral-subdued"
                id={{this.suggestedId}}
              >
                {{t "general.ask_ernest.suggested"}}
              </p>

              <ul
                aria-labelledby={{this.suggestedId}}
                class="flex flex-col gap-200"
              >
                {{#each this.promptLinks key="id" as |prompt|}}
                  <li>
                    <a
                      class={{styles.prompt}}
                      data-test-id="ask-ernest-prompt"
                      data-test-prompt-id={{prompt.id}}
                      href={{prompt.href}}
                      rel="noopener noreferrer"
                      target="_blank"
                      {{on "click" (fn this.trackPrompt dropdown prompt)}}
                    >
                      <span class="text-300--medium flex-1">
                        {{t prompt.label}}
                        <span class="sr-only">
                          {{t "general.ask_ernest.opens_in_new_tab"}}
                        </span>
                      </span>
                      <PrismIcon
                        @color="intelligence"
                        @name="link-out"
                        @size="small"
                        aria-hidden="true"
                      />
                    </a>
                  </li>
                {{/each}}
              </ul>
            </div>

            <a
              class="prism-button prism-button--ghost prism-button--small
                {{styles.other}}"
              data-test-id="ask-ernest-other"
              href={{this.blankChatHref}}
              rel="noopener noreferrer"
              target="_blank"
              {{on "click" (fn this.trackOtherQuestion dropdown)}}
            >
              {{t "general.ask_ernest.other_question"}}
              <span class="sr-only">
                {{t "general.ask_ernest.opens_in_new_tab"}}
              </span>
            </a>
          </div>
        </div>
      </GeneralDropdownsDropdownYield>
    </div>
  </template>
}
